/**
 * Shared validation for "Send for Review":
 * - Assets are not required to have equipment; projects may send for review with some or all assets without equipment rows
 * - Any equipment that exists must be assigned to a resource (Site Engineer or Site Supervisor)
 * - Assigned resources without any equipment do not block submission (no warning)
 */

import projectAssetService from '../../../services/projectAssetService';
import { equipmentService } from '../../../services/equipmentService';
import projectService from '../../../services/projectService';

function normalizeId(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'object' && value !== null) {
    const o = value as Record<string, unknown>;
    if (o._id) return String(o._id);
    if (o.id) return String(o.id);
    if (o.$oid) return String(o.$oid);
  }
  return undefined;
}

export interface ValidateProjectForReviewInput {
  projectId: string;
  project?: Record<string, unknown>;
  assets?: unknown[];
  equipment?: unknown[];
}

export interface ValidateProjectForReviewResult {
  valid: boolean;
  errors: string[];
}

/**
 * Validates that a project is ready to send for review:
 * 1. At least one asset
 * 2. Every equipment (if any) is assigned to a resource (siteEngineerId or siteSupervisorId)
 * 3. At least one assigned resource (Project Manager, Project Head, Site Supervisor, or Site Engineer)
 */
export async function validateProjectForReviewSend(
  input: ValidateProjectForReviewInput
): Promise<ValidateProjectForReviewResult> {
  const errors: string[] = [];
  const { projectId, project: projectSnapshot, assets: assetsInput, equipment: equipmentInput } = input;

  let project = projectSnapshot;
  let assets: unknown[] = assetsInput ?? [];
  let equipment: unknown[] = equipmentInput ?? [];

  if (!project) {
    try {
      project = (await projectService.getProjectById(projectId)) as Record<string, unknown>;
    } catch (e) {
      errors.push('Could not load project. Please try again.');
      return { valid: false, errors };
    }
  }

  if (assets.length === 0) {
    try {
      const list = await projectAssetService.getProjectAssetsWithDetails(projectId);
      assets = Array.isArray(list) ? list : [];
    } catch (e) {
      errors.push('Could not load project assets. Please try again.');
      return { valid: false, errors };
    }
  }

  if (equipment.length === 0) {
    try {
      const list = await equipmentService.getByProject(projectId);
      equipment = Array.isArray(list) ? list : (list && typeof list === 'object' && (list as { data?: unknown[] }).data ? (list as { data: unknown[] }).data : []);
    } catch (e) {
      errors.push('Could not load project equipment. Please try again.');
      return { valid: false, errors };
    }
  }

  // 1. At least one asset
  if (assets.length === 0) {
    errors.push('Project must have at least one asset before sending for review.');
  }

  // 2. Every equipment assigned to a resource; and collect resource IDs that have equipment
  const assignedResourceIdsFromEquipment = new Set<string>();
  const equipmentWithoutResources: string[] = [];
  for (const eq of equipment) {
    const e = eq as Record<string, unknown>;
    const assignedTo = e.assignedTo ?? e.siteEngineerId ?? e.siteSupervisorId ?? e.engineerId;
    const resourceId = normalizeId(
      (assignedTo as Record<string, unknown> | undefined)?._id ?? (assignedTo as Record<string, unknown>)?.id ?? assignedTo
    );
    const eqName = (e.equipmentName ?? e.name ?? e.equipmentCode ?? 'Unnamed equipment') as string;
    if (resourceId) {
      assignedResourceIdsFromEquipment.add(resourceId);
    } else {
      equipmentWithoutResources.push(eqName);
    }
  }
  if (equipmentWithoutResources.length > 0) {
    errors.push(
      `Every equipment must be assigned to a resource (Site Engineer or Site Supervisor). ${equipmentWithoutResources.length} equipment item(s) are unassigned.`
    );
  }

  // 3. At least one assigned resource
  const assignedResources = project?.assignedResources as Record<string, unknown> | undefined;
  const assignedResourceIdsFromProject = new Set<string>();

  const pushResourceId = (id: string | undefined) => {
    if (!id) return;
    assignedResourceIdsFromProject.add(id);
  };

  if (assignedResources) {
    const pm = assignedResources.projectManager;
    pushResourceId(normalizeId((pm as Record<string, unknown> | undefined)?._id ?? (pm as Record<string, unknown>)?.id ?? pm));
    const ph = assignedResources.projectHead;
    pushResourceId(normalizeId((ph as Record<string, unknown> | undefined)?._id ?? (ph as Record<string, unknown>)?.id ?? ph));
    const sups = assignedResources.siteSupervisors as unknown[] | undefined;
    if (Array.isArray(sups)) {
      sups.forEach((s: unknown) => {
        const sid = normalizeId((s as Record<string, unknown> | undefined)?._id ?? (s as Record<string, unknown>)?.id ?? s);
        pushResourceId(sid);
      });
    }
    const engs = assignedResources.siteEngineers as unknown[] | undefined;
    if (Array.isArray(engs)) {
      engs.forEach((s: unknown) => {
        const eid = normalizeId((s as Record<string, unknown> | undefined)?._id ?? (s as Record<string, unknown>)?.id ?? s);
        pushResourceId(eid);
      });
    }
  }

  if (assignedResourceIdsFromProject.size === 0) {
    errors.push(
      'Project must have at least one assigned resource (Project Manager, Project Head, Site Supervisor, or Site Engineer) before sending for review.'
    );
  }

  return {
    valid: errors.length === 0,
    errors
  };
}
